import { useState, type FormEvent, type JSX } from 'react';
import { useAuthStore } from './authStore';
import { makeApi } from '../../lib/api';

/**
 * Dev-token-paste login fallback.
 *
 * The editor pastes a Sanctum bearer token issued server-side
 * (`php artisan tinker; $space->issueToken('admin', ['*'])`), the
 * shell validates it against `/v1/whoami`, and if it works, persists
 * it to localStorage and unlocks the editor. The normal flow is the
 * email + password `LoginForm` — this is kept around for ops use
 * (servers without working SMTP, debugging auth, etc.).
 */
export default function TokenPasteShell({
  onSwitchToSignup,
  onSwitchToLogin,
}: {
  onSwitchToSignup?: () => void;
  onSwitchToLogin?: () => void;
}): JSX.Element {
  const setToken = useAuthStore((s) => s.setToken);
  const setIdentity = useAuthStore((s) => s.setIdentity);

  const [input, setInput] = useState<string>('');
  const [status, setStatus] = useState<
    | { kind: 'idle' }
    | { kind: 'verifying' }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const candidate = input.trim();
    if (candidate.length === 0) {
      setStatus({ kind: 'error', message: 'Token erforderlich' });
      return;
    }

    setStatus({ kind: 'verifying' });

    const api = makeApi(candidate);
    const result = await api.whoami();

    switch (result.status) {
      case 'ok':
        setToken(candidate);
        setIdentity(result.data.space_slug, result.data.abilities);
        // The auth store change re-renders App.tsx which dispatches
        // to the EditorShell; nothing else to do here.
        return;
      case 'unauthenticated':
        setStatus({
          kind: 'error',
          message: 'Token von /v1/whoami abgelehnt. Prüfe ob er korrekt ausgestellt wurde.',
        });
        return;
      case 'forbidden':
        setStatus({
          kind: 'error',
          message: `Kein Zugriff: ${result.code}`,
        });
        return;
      case 'not-found':
        setStatus({
          kind: 'error',
          message: '/v1/whoami nicht gefunden — läuft die API unter der konfigurierten baseUrl?',
        });
        return;
      case 'error':
        setStatus({
          kind: 'error',
          message: `HTTP ${result.code} von der API`,
        });
        return;
    }
  }

  return (
    <main className="grid min-h-screen place-items-center bg-zinc-50 px-4">
      <form
        onSubmit={(e) => {
          handleSubmit(e).catch((err) => {
            setStatus({
              kind: 'error',
              message: err instanceof Error ? err.message : String(err),
            });
          });
        }}
        className="w-full max-w-md rounded-xl border border-zinc-200 bg-white p-8 shadow-sm"
      >
        <header className="mb-6">
          <h1 className="text-xl font-semibold tracking-tight text-zinc-900">
            Mit API-Token anmelden
          </h1>
          <p className="mt-1 text-sm text-zinc-600">
            Entwickler-Fallback. Sanctum-API-Token einfügen, ausgestellt via{' '}
            <code className="rounded bg-zinc-100 px-1 font-mono text-xs">
              php artisan tinker
            </code>
            . Für den regulären E-Mail-/Passwort-Flow: zum{' '}
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="underline hover:text-zinc-900"
            >
              Standard-Login
            </button>
            .
          </p>
        </header>

        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">
            Bearer-Token
          </span>
          <input
            type="password"
            autoComplete="off"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="1|xxxxxxxxxxxxxxxx..."
            className="w-full rounded-md border border-zinc-300 px-3 py-2 font-mono text-sm focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900"
            disabled={status.kind === 'verifying'}
            spellCheck={false}
            data-testid="token-input"
          />
        </label>

        {status.kind === 'error' ? (
          <p
            role="alert"
            className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900"
          >
            {status.message}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={status.kind === 'verifying' || input.trim().length === 0}
          className="mt-6 w-full rounded-md bg-zinc-900 px-4 py-2 font-medium text-white shadow-sm transition disabled:cursor-not-allowed disabled:bg-zinc-300"
        >
          {status.kind === 'verifying' ? 'Prüfe…' : 'Anmelden'}
        </button>

        <p className="mt-6 text-xs text-zinc-500">
          Noch keinen Token? Auf dem API-Container in{' '}
          <code className="rounded bg-zinc-100 px-1 font-mono">tinker</code> ausführen:{' '}
          <code className="rounded bg-zinc-100 px-1 font-mono">
            $space-&gt;issueToken('local', ['*'])-&gt;plainTextToken
          </code>
          .
        </p>

        {onSwitchToSignup !== undefined ? (
          <p className="mt-3 text-xs text-zinc-500">
            Account-Anfrage:{' '}
            <a
              href="mailto:peacock@webhoch.com?subject=Peacock%20Frühzugang"
              className="text-zinc-900 underline hover:text-zinc-700"
            >
              peacock@webhoch.com
            </a>
            . Closed Pre-Alpha — Self-Signup folgt mit dem Public-Launch.
          </p>
        ) : null}
      </form>
    </main>
  );
}
